import { useMemo, useState } from 'react';
import { Edit, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PendingChangesAlert } from '@/components/shared/pending-changes-alert';
import { PolicyDetailSheet } from './components/policy-detail-sheet';
import { PoliciesTable } from './components/policies-table';
import type { PolicyWithChange } from './utils/policyProposalUtils';
import { EditPoliciesDialog } from './components/edit-policies-dialog';
import { ReviewPolicyChangesDialog } from './components/review-policy-changes-dialog';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useGetPolicyProposal } from '@/hooks/admin/policy/use-get-policy-proposal';
import { useListPolicies } from '@/hooks/policy/use-list-policies';
import { useAccounts } from '@/hooks/account/useAccounts';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageAction,
  PageHeaderInfo,
  PageContent,
} from '@/components/layout/page-header';
import { ReorderError } from '@/components/shared/reorder-error';
import { useOrganizationItemOrder } from '@/hooks/admin/organization/use-organization-item-order';

const getPolicyId = (policy: PolicyWithChange) => policy.id;

export function PoliciesPage() {
  const { organization } = useMyOrganization();
  if (!organization) {
    throw new Error('Organization not found');
  }
  const isAdmin = organization.isAdmin;

  const {
    proposal,
    isLoading: isLoadingProposal,
    mutate: loadProposal,
  } = useGetPolicyProposal();

  const {
    policies,
    isLoading: isLoadingPolicies,
    mutate: loadPolicies,
  } = useListPolicies(organization.id);

  const { accounts } = useAccounts();

  const [selectedPolicy, setSelectedPolicy] = useState<PolicyWithChange | null>(
    null
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isEditPoliciesDialogOpen, setIsEditPoliciesDialogOpen] =
    useState(false);
  const [isReviewChangesDialogOpen, setIsReviewChangesDialogOpen] =
    useState(false);

  // Convert policies to PolicyWithChange for the table (with no pending changes)
  const policiesWithChange: PolicyWithChange[] = useMemo(
    () =>
      policies
        ? policies.map((policy) => ({
            ...policy,
            change: null,
          }))
        : [],
    [policies]
  );
  const {
    orderedItems: orderedPolicies,
    reorder: reorderPolicies,
    isReordering,
    reorderError,
  } = useOrganizationItemOrder({
    items: policiesWithChange,
    itemType: 'policies',
    getItemId: getPolicyId,
    enabled: isAdmin,
  });

  const handleRowClick = (policy: PolicyWithChange) => {
    setSelectedPolicy(policy);
    setIsSheetOpen(true);
  };

  const handleProposalCreated = async () => {
    // Refresh the proposal and close the edit dialog
    await loadProposal();
    setIsEditPoliciesDialogOpen(false);
  };

  const handleReviewChanges = () => {
    setIsReviewChangesDialogOpen(true);
  };

  const handleProposalApplied = async () => {
    // Refresh the proposal and policies list after approval/rejection/execution
    await Promise.all([loadProposal(), loadPolicies()]);
  };

  // Check if there is a pending proposal
  // Use != to check for both null and undefined (SWR initial state)
  const hasPendingProposal = isAdmin && !isLoadingProposal && proposal != null;

  return (
    <div className="">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Policies</PageTitle>
          <PageDescription>
            Manage your organization's transaction policies
          </PageDescription>
        </PageHeaderInfo>
        <PageAction>
          <div className="flex flex-col items-end gap-4">
            {hasPendingProposal && (
              <PendingChangesAlert onReviewChanges={handleReviewChanges} />
            )}
            {isAdmin && !hasPendingProposal && (
              <Button onClick={() => setIsEditPoliciesDialogOpen(true)}>
                <Edit className="h-4 w-4 mr-1" />
                Add/Edit Policies
              </Button>
            )}
          </div>
        </PageAction>
      </PageHeader>

      <PageContent>
        <ReorderError message={reorderError} />
        {isLoadingPolicies ? (
          <div className="h-48 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading
            policies...
          </div>
        ) : (
          <PoliciesTable
            policies={orderedPolicies}
            onRowClick={handleRowClick}
            showEditStateBadges={false}
            emptyMessage="No policies yet. Create a policy to get started."
            isReorderable={isAdmin}
            isReordering={isReordering}
            onReorder={reorderPolicies}
          />
        )}

        <PolicyDetailSheet
          policy={selectedPolicy}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
        />

        <EditPoliciesDialog
          open={isEditPoliciesDialogOpen}
          onOpenChange={setIsEditPoliciesDialogOpen}
          policies={policies || []}
          accounts={accounts || []}
          onProposalCreated={handleProposalCreated}
        />

        {isAdmin && proposal && (
          <ReviewPolicyChangesDialog
            open={isReviewChangesDialogOpen}
            onOpenChange={setIsReviewChangesDialogOpen}
            proposal={proposal}
            policies={policies || []}
            accounts={accounts || []}
            onApplied={handleProposalApplied}
            refreshProposal={async () => {
              await loadProposal();
            }}
          />
        )}
      </PageContent>
    </div>
  );
}
